
/**
 * Searchable Select (vanilla) – ES6 Module
 * Progressive Enhancement: Verwendet einen Combobox-Input über einem nativen <select>.
 * - Filtert clientseitig anhand data-label/textContent.
 * - Keyboard: ↑ ↓ Enter Escape
 * - Barrierearm mit ARIA-Attributen.
 */

export function initSearchableSelect(root = document) {
    const components = root.querySelectorAll('[data-module="searchable-select"]');
    components.forEach(setupSearchableSelect);
}


function setupSearchableSelect(el) {
    if (el.dataset.initialized === 'true') return;
    el.dataset.initialized = 'true';

    const input     = el.querySelector('.searchableSelect__input');
    const list      = el.querySelector('.searchableSelect__list');
    const select    = el.querySelector('select.searchableSelect__native');
    const clearBtn  = el.querySelector('.searchableSelect__clear');
    const combobox  = el.querySelector('.searchableSelect__combobox');

    if (!input || !list || !select) return;

    const options = [...select.options]
        .map((opt, i) => ({
            value: opt.value,
            label: opt.dataset.label || opt.textContent.trim(),
            disabled: opt.disabled,
            index: i
        }))
        .filter(o => o.value !== '' && !o.disabled);

    let filtered = options.slice();
    let activeIndex = -1;
    let isOpen = false; // 👈 NEU: Anfangs geschlossen

    const normalize = (s) =>
        s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

    function highlight(label, q) {
        const nLabel = normalize(label);
        const nQ = normalize(q);
        const idx = nLabel.indexOf(nQ);
        if (!q || idx === -1) return label;
        const start = label.slice(0, idx);
        const match = label.slice(idx, idx + q.length);
        const end   = label.slice(idx + q.length);
        return `${start}<mark>${match}</mark>${end}`;
    }

    function render() {
        list.innerHTML = '';

        // ⛔️ Liste nur rendern, wenn offen
        if (!isOpen || !filtered.length) {
            combobox.setAttribute('aria-expanded', 'false');
            input.removeAttribute('aria-activedescendant');
            return;
        }

        const frag = document.createDocumentFragment();

        filtered.forEach((o, i) => {
            const li = document.createElement('li');
            li.id = `${select.id}_opt_${i}`;
            li.role = 'option';
            li.tabIndex = -1;
            li.className = 'searchableSelect__option';
            li.dataset.value = o.value;
            li.innerHTML = highlight(o.label, input.value.trim());
            if (i === activeIndex) li.classList.add('is-active');
            frag.appendChild(li);
        });

        list.appendChild(frag);
        combobox.setAttribute('aria-expanded', 'true');
        input.setAttribute('aria-activedescendant',
            activeIndex >= 0 ? `${select.id}_opt_${activeIndex}` : '');
    }

    function filter(q) {
        const nQ = normalize(q);
        filtered = nQ
            ? options.filter(o =>
                normalize(o.label).includes(nQ) ||
                o.value.toLowerCase().startsWith(q.toLowerCase())
            )
            : options.slice();

        activeIndex = filtered.length ? 0 : -1;
        render();
    }

    function openList() {
        if (!isOpen) {
            isOpen = true;
            // Bei Öffnen die aktuelle Eingabe verwenden
            filter(input.value.trim());
        }
    }

    function closeList() {
        if (isOpen) {
            isOpen = false;
            render();
        }
    }

    function commit(value) {
        const match = options.find(o => o.value === value);
        if (!match) return;
        select.value = match.value;
        input.value = match.label;
        input.setAttribute('data-selected', 'true');
        closeList();

        const evt = new Event('change', { bubbles: true });
        select.dispatchEvent(evt);
    }



    // 🔹 Helper: Clear-Button je nach Inhalt zeigen/verstecken
    function updateClearVisibility() {
        const hasText = input.value.trim().length > 0;
        if (!clearBtn) return;
        if (hasText) {
            clearBtn.classList.add('is-visible');
            clearBtn.setAttribute('aria-hidden', 'false');
            clearBtn.tabIndex = 0;
        } else {
            clearBtn.classList.remove('is-visible');
            clearBtn.setAttribute('aria-hidden', 'true');
            clearBtn.tabIndex = -1;
        }
    }


    // Events
    input.addEventListener('focus', () => {
        // 👇 Öffnen erst bei Fokus (Klick ins Feld)
        openList();
        updateClearVisibility();
    });

    input.addEventListener('input', (e) => {
        input.removeAttribute('data-selected');
        // Tippen hält Liste offen und filtert
        if (!isOpen) isOpen = true;
        filter(e.target.value.trim());
        updateClearVisibility();
    });


    // Beim Auswählen aus der Liste
    function commit(value) {
        const match = options.find(o => o.value === value);
        if (!match) return;
        select.value = match.value;
        input.value = match.label;
        input.setAttribute('data-selected', 'true');
        closeList();

        // Change-Event für deine Ajax-Listener
        const evt = new Event('change', { bubbles: true });
        select.dispatchEvent(evt);

        updateClearVisibility(); // 🔸 jetzt ist was im Feld → Button sichtbar
    }


    input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!isOpen) openList();
            if (!filtered.length) return;
            activeIndex = Math.min(activeIndex + 1, filtered.length - 1);
            render();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!isOpen) openList();
            if (!filtered.length) return;
            activeIndex = Math.max(activeIndex - 1, 0);
            render();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && filtered[activeIndex]) {
                commit(filtered[activeIndex].value);
            }
        } else if (e.key === 'Escape') {
            closeList();
        }
    });

    list.addEventListener('click', (e) => {
        const li = e.target.closest('.searchableSelect__option');
        if (!li) return;
        commit(li.dataset.value);
    });

    clearBtn?.addEventListener('click', () => {
        input.value = '';
        input.removeAttribute('data-selected');
        select.value = '';
        // Nach dem Clear geschlossen lassen, bis Fokus/Tippen
        filtered = options.slice();
        activeIndex = filtered.length ? 0 : -1;
        closeList();
        updateClearVisibility(); // 🔸 leer → Button versteckt
        input.focus();
    });

    // Initialisierung: Feld-Inhalt spiegeln, dann Clear-Button korrekt setzen
    if (select.value) {
        const cur = options.find(o => o.value === select.value);
        input.value = cur ? cur.label : '';
    } else {
        input.value = '';
    }


    // Outside click schließt Liste
    document.addEventListener('click', (ev) => {
        if (!el.contains(ev.target)) {
            closeList();
        }
    });



    // ⛔️ WICHTIG: Keine automatische Öffnung beim Init
    filtered = options.slice();
    activeIndex = filtered.length ? 0 : -1;
    combobox.setAttribute('aria-expanded', 'false');
    // render() nicht aufrufen, damit die Liste zu bleibt'

    updateClearVisibility();

    // Outside click schließt Liste (falls im Code vorhanden)
    document.addEventListener('click', (ev) => {
        if (!el.contains(ev.target)) {
            closeList();
        }
    });

}

